import { AxiosResponse } from 'axios';

import { initializeApiInterceptors } from './axiosInterceptors';
import { HttpClient } from './httpClient';

export type ResponseHandler<T = any, R = T> = (response: AxiosResponse<T>) => R;

interface ApiServiceGetConfig {
	params: any;
	headers?: any;
}

/**
 * Pull a human-readable message from an Axios-style error or raw response body.
 */
function messageFromResponseBody(body: unknown): string | null {
	if (body == null) return null;
	if (typeof body === 'string' && body.trim()) return body.trim();
	if (typeof body === 'object') {
		const o = body as Record<string, unknown>;
		const candidate =
			o['hydra:description'] || o.detail || o.message || o.error || o.title;
		if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
	}
	return null;
}

/**
 * Validates that the response status is in the 2xx–3xx range.
 * Throws a real Error (never `undefined`) so callers can read `.message`.
 */
function statusChecker<T>(response: AxiosResponse<T>) {
	// Accept 2xx (success) and 3xx (redirect) status codes as successful
	if (response?.status >= 200 && response?.status < 400) {
		return response;
	}

	const body: any = response?.data;
	const message =
		messageFromResponseBody(body) ||
		(response?.status ? `Request failed with status ${response.status}` : 'Request failed');

	const err = new Error(message);
	(err as any).response = response;
	(err as any).data = body;
	throw err;
}

/**
 * Normalise thrown values from axios / statusChecker into an Error with a usable message.
 * Axios errors keep a generic `.message` ("Request failed with status code 422"); prefer the
 * response body when present (e.g. plain-text "Access Denied...").
 */
export function normaliseApiError(error: unknown): Error {
	if (error && typeof error === 'object') {
		const anyErr = error as any;
		const body = anyErr?.response?.data ?? (anyErr instanceof Error ? undefined : anyErr);
		const fromBody = messageFromResponseBody(body);

		if (fromBody) {
			const err = new Error(fromBody);
			(err as any).data = body;
			(err as any).response = anyErr?.response;
			(err as any).status = anyErr?.response?.status ?? anyErr?.status;
			return err;
		}

		if (error instanceof Error) {
			return error;
		}

		const message =
			anyErr?.['hydra:description'] ||
			anyErr?.detail ||
			anyErr?.message ||
			anyErr?.error ||
			'Request failed';
		const err = new Error(String(message));
		(err as any).data = body ?? anyErr;
		(err as any).response = anyErr?.response;
		return err;
	}
	return new Error('Request failed');
}

/**
 * Primary API service for all standard application requests.
 * Wraps the shared primary {@link HttpClient} instance and provides
 * typed convenience methods for GET, POST, PUT, PATCH, DELETE and file upload.
 *
 * Attach request/response interceptors via {@link initializeApiInterceptors},
 * which is called automatically in the constructor.
 *
 * @example
 * const data = await apiService.get('/users', { params: { page: 1 } });
 */
class ApiService {
	private _httpClient = HttpClient.getInstance();
	private _customBaseUrl: string | null = null;

	constructor() {
		initializeApiInterceptors(this._httpClient);
	}

	/** Default response handler — extracts `data` from the Axios response. Returns `null` when data is absent. */
	responseHandler: ResponseHandler = ({ data }) => data ?? null;

	/**
	 * Override the base URL at runtime.
	 * @param baseUrl - The new base URL to use for subsequent requests.
	 */
	setBaseUrl(baseUrl: string) {
		this._customBaseUrl = baseUrl;
		// Note: HttpClient.getInstance() is used, so we can't recreate it here
		// The base URL change would need to be handled at the HttpClient level if needed
	}

	/**
	 * Returns the currently active base URL.
	 * Falls back to `REACT_APP_API_URL` when no custom URL has been set.
	 */
	getBaseUrl(): string {
		return this._customBaseUrl || process.env.REACT_APP_API_URL || '';
	}

	/**
	 * Performs an HTTP GET request.
	 * @param url - Endpoint path relative to the base URL.
	 * @param config - Optional query params and custom headers.
	 */
	async get<T = any>(url: string, config?: ApiServiceGetConfig): Promise<AxiosResponse<T>> {
		const { params, headers } = config ?? {};
		try {
			return statusChecker(await this._httpClient.get<T>(url, { params, headers }));
		} catch (error) {
			throw normaliseApiError(error);
		}
	}

	/**
	 * Performs an HTTP POST request.
	 * @param url - Endpoint path relative to the base URL.
	 * @param body - Optional request body.
	 * @param headers - Optional additional headers.
	 */
	async post<T = any, B = any>(url: string, body?: B, headers?: any): Promise<AxiosResponse<T>> {
		try {
			return statusChecker(await this._httpClient.post<T>(url, body, { headers }));
		} catch (error) {
			throw normaliseApiError(error);
		}
	}

	/**
	 * Performs a multipart/form-data POST request for file uploads.
	 * @param url - Endpoint path relative to the base URL.
	 * @param body - FormData or multipart body.
	 */
	async postUpload<T = any, B = any>(url: string, body?: B): Promise<AxiosResponse<T>> {
		try {
			return statusChecker(
				await this._httpClient.post<T>(url, body, {
					headers: { 'Content-Type': 'multipart/form-data' },
				})
			);
		} catch (error) {
			throw normaliseApiError(error);
		}
	}

	/**
	 * Performs an HTTP PUT request (full resource replacement).
	 * @param url - Endpoint path relative to the base URL.
	 * @param body - Full resource body.
	 */
	async put<T = any, B = any>(url: string, body: B): Promise<AxiosResponse<T>> {
		try {
			return statusChecker(await this._httpClient.put<T>(url, body));
		} catch (error) {
			throw normaliseApiError(error);
		}
	}

	/**
	 * Performs an HTTP DELETE request.
	 * @param url - Endpoint path relative to the base URL.
	 */
	async delete(url: string): Promise<AxiosResponse<null>> {
		try {
			return statusChecker(await this._httpClient.delete(url));
		} catch (error) {
			throw normaliseApiError(error);
		}
	}

	/**
	 * Performs an HTTP PATCH request using `application/merge-patch+json`.
	 * @param url - Endpoint path relative to the base URL.
	 * @param body - Partial resource body containing only the fields to update.
	 */
	async patch<T = any, B = any>(url: string, body: B): Promise<AxiosResponse<T>> {
		try {
			return statusChecker(
				await this._httpClient.patch<T>(url, body, {
					headers: { 'Content-Type': 'application/merge-patch+json' },
				})
			);
		} catch (error) {
			throw normaliseApiError(error);
		}
	}
}

export const apiService = new ApiService();
